/**
 * The person's plan change requests, kept by the sandbox gateway (vfs-service src/gateway/planRequests) under the vfs
 * path the web app already uses, with the person's authorized fetch:
 * `GET /api/v1/me/plan-requests` -> `{ current, plans, pending, recent }`, `POST /api/v1/me/plan-requests`
 * `{ plan, reason }` (the same, with the new request pending), `POST /api/v1/me/plan-requests/:id/cancel`; and
 * `GET /api/v1/me/plans` -> `{ current, credits, change, pending, plans }`, the plans side by side for "Change plan"
 * (each with its estimated cost per hour in credits, capabilities and difference against the person's plan).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { shareInflight } from '../utils/sharedRequest';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');

async function call(path, { method = 'GET', body, root = '/api/v1/me/plan-requests' } = {}) {
  if (!base()) { return null; }
  const response = await getAuthorizedFetch()(`${base()}${root}${path}`, {
    method,
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  // A gateway without plan requests (an older one, or shared mode): nothing to show.
  if (response.status === 404 && method === 'GET') { return null; }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

/** `{ current, plans, pending, recent }`, or null where the gateway has no plan requests. */
export function getPlanRequests() {
  return shareInflight('plan-requests', () => call(''));
}

export function askForPlan(plan, reason = '') {
  return call('', { method: 'POST', body: { plan, reason } });
}

export function cancelPlanRequest(id) {
  return call(`/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
}

/** The plans side by side, or null where the gateway has no catalog (an older one: the dialog lists the plans). */
export function getPlanCatalog() {
  return shareInflight('plan-catalog', () => call('', { root: '/api/v1/me/plans' }));
}
